import { useCallback, useMemo } from "react"
import { useSearchParams } from "react-router"
import type { SortOrder } from "@/shared/types/SortOrder"
import type { RelationshipSubType, RelationshipType } from "../types/Customer"

const DEFAULT_PAGE = 0
const DEFAULT_PAGE_SIZE = 20
const DEFAULT_SORT_BY = "name"

interface CustomerQueryState {
  page: number
  pageSize: number
  searchTerm: string | undefined
  sortBy: string
  sortOrder: SortOrder | undefined
  salesforceId: string | undefined
  relationshipType: RelationshipType | undefined
  relationshipSubType: RelationshipSubType | undefined
  sdmEmail: string | undefined
}

function parsePageNumber(value: string | null, defaultValue: number): number {
  const parsed = parseInt(value ?? "", 10)
  // biome-ignore lint/suspicious/noGlobalIsNan: Need to check for NaN result from parseInt, not just NaN values
  return isNaN(parsed) || parsed < 0 ? defaultValue : parsed
}

function parseCustomerQueryState(searchParams: URLSearchParams): CustomerQueryState {
  return {
    page: parsePageNumber(searchParams.get("page"), DEFAULT_PAGE),
    pageSize: parsePageNumber(searchParams.get("pageSize"), DEFAULT_PAGE_SIZE),
    salesforceId: searchParams.get("salesforceId") ?? undefined,
    relationshipType: (searchParams.get("relationshipType") as RelationshipType) ?? undefined,
    relationshipSubType: (searchParams.get("relationshipSubType") as RelationshipSubType) ?? undefined,
    searchTerm: searchParams.get("searchTerm") ?? undefined,
    sdmEmail: searchParams.get("sdmEmail") ?? undefined,
    sortBy: searchParams.get("sortBy") ?? DEFAULT_SORT_BY,
    sortOrder: (searchParams.get("sortOrder") as SortOrder) ?? undefined
  }
}

export function useCustomerQueryState() {
  const [searchParams, setSearchParams] = useSearchParams()

  const state = useMemo(() => parseCustomerQueryState(searchParams), [searchParams])

  const updateParams = useCallback(
    (updates: Partial<CustomerQueryState> | ((prev: CustomerQueryState) => Partial<CustomerQueryState>)) => {
      // Derive current state from *current* URL params for functional updates.
      // This avoids a mount-time effect and keeps URL as the single source of truth.
      const currentState = parseCustomerQueryState(searchParams)

      const resolvedUpdates = typeof updates === "function" ? updates(currentState) : updates

      const nextParams = new URLSearchParams(searchParams)
      for (const [key, value] of Object.entries(resolvedUpdates)) {
        // Remove param if empty/undefined OR if page=0 (default)
        if (value === "" || value === undefined || (key === "page" && value === 0)) {
          nextParams.delete(key)
        } else {
          nextParams.set(key, value.toString())
        }
      }

      // Avoid redundant navigation/rerenders if nothing actually changed.
      if (nextParams.toString() === searchParams.toString()) return

      setSearchParams(nextParams, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  return {
    ...state,
    updateParams
  }
}
